import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiClient } from '../services/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type PlatformFeatureKey = 'resume-review' | 'culture-call' | 'referral-session';

export const ALL_PLATFORM_FEATURE_KEYS: PlatformFeatureKey[] = [
  'resume-review',
  'culture-call',
  'referral-session',
];

interface PlatformFeaturesContextType {
  /** Keys of features that are currently enabled. Defaults to all-enabled on error. */
  enabledFeatures: PlatformFeatureKey[];
  /** True while the initial fetch is in flight. */
  loading: boolean;
  /** Returns true if the given feature key is currently enabled. */
  isFeatureEnabled: (key: PlatformFeatureKey) => boolean;
  /** Manually re-fetch from Supabase (e.g. after admin changes). */
  refresh: () => Promise<void>;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const PlatformFeaturesContext = createContext<PlatformFeaturesContextType>({
  enabledFeatures: [...ALL_PLATFORM_FEATURE_KEYS],
  loading: false,
  isFeatureEnabled: () => true,
  refresh: async () => {},
});

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export const PlatformFeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Fail-open: default to all features enabled so a Supabase hiccup never
  // accidentally hides services from users.
  const [enabledFeatures, setEnabledFeatures] = useState<PlatformFeatureKey[]>([
    ...ALL_PLATFORM_FEATURE_KEYS,
  ]);
  const [loading, setLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await apiClient.getPlatformFeatures();
      if (!response.success || !response.data) {
        return;
      }

      if (response.data.length > 0) {
        const enabled = response.data
          .filter((row) => row.enabled)
          .map((row) => row.feature_key as PlatformFeatureKey);
        setEnabledFeatures(enabled);
      }
    } catch {
      // Fail-open: keep all features enabled on any error.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchFeatures();
  }, [fetchFeatures]);

  // Re-fetch when the tab regains focus so changes made in the admin panel
  // propagate without requiring a full page reload.
  useEffect(() => {
    const handleFocus = () => {
      void fetchFeatures();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchFeatures]);

  const isFeatureEnabled = useCallback(
    (key: PlatformFeatureKey) => enabledFeatures.includes(key),
    [enabledFeatures],
  );

  return (
    <PlatformFeaturesContext.Provider
      value={{ enabledFeatures, loading, isFeatureEnabled, refresh: fetchFeatures }}
    >
      {children}
    </PlatformFeaturesContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

export function usePlatformFeatures(): PlatformFeaturesContextType {
  return useContext(PlatformFeaturesContext);
}

export function useIsFeatureEnabled(key: PlatformFeatureKey): boolean {
  const { isFeatureEnabled } = usePlatformFeatures();
  return isFeatureEnabled(key);
}

export default PlatformFeaturesContext;
